<template>
  <div v-if="applyToAllColumnsEnabled" class="binSelection" style="display: flex; align-items: center; gap: 8px;">
    <template v-if="showingCheckbox">
      <Checkbox binary :modelValue="areAllColumnsSelected" @update:modelValue="setAllColumnsSelected">
        {{ checkboxLabel }}
      </Checkbox>
    </template>
    <template v-else>
      <div style="white-space: nowrap;">
        Apply to
      </div>
      <InputGroup>
        <Dropdown
          v-model="columnSelectionCode"
          :options="columnSelectionOptions"
          :optionValue="'value'"
          :optionLabel="'label'"
        ></Dropdown>
        <Button v-if="isBinSetting" outlined severity="secondary" @click="showingDialog = true">
          <i class="pi pi-ellipsis-h" style="font-size: small; color: #666;"></i>
        </Button>
      </InputGroup>
    </template>
  </div>
  <BinSelectionDialog v-model:showingDialog="showingDialog"></BinSelectionDialog>
</template>

<script setup>
import { computed, ref } from "vue";
import { watchImmediate } from "@vueuse/core";
import { map_dropNulls } from "./utility.js";
import BinSelectionDialog from "./BinSelectionDialog.vue";

let props = defineProps([
  "tag",
  "columnSelectionCode",
  "applyToAllColumnsEnabled"
]);

let showingDialog = ref(false);
let columnSelectionCode = defineModel("columnSelectionCode");
let applyToAllColumnsEnabled = computed(() => props.applyToAllColumnsEnabled);
let isBinSetting = computed(() => {
  let system = props.tag.$session.system;
  let bin = props.tag.$parents().find(parent => system.bins.children.includes(parent));
  return bin != null;
});

let columnSelectionOptions = computed(() => {
  let system = props.tag.$session.system;
  let bin = props.tag.$parents().find(parent => system.bins.children.includes(parent));
  let isSystemSetting = (bin == null);

  let options = [];
  options.push({
    value: "selectedColumn",
    label: isSystemSetting ? system.boardName.value : bin.name.value
  });

  let isBinSetting = (bin != null);
  if (isBinSetting) {
    bin.binGroups.value.forEach(groupName => {
      options.push({
        label: groupName,
        value: groupName
      });
    });
  };
  
  let systems = map_dropNulls(store.sessions, session => session.system);
  let shouldAddBoardOption = isBinSetting && systems.length > 1;
  if (shouldAddBoardOption) {
    options.push({
      label: system.boardName.value,
      value: system.boardName.value
    });
  }

  options.sort((a, b) => {
    return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" });
  });

  options.push({
    value: "allColumns",
    label: isSystemSetting ? "All systems" : "All bins"
  });

  return options;
});

// Reset the columns selection when the previously selected option no longer exists.
watchImmediate(columnSelectionOptions, () => {
  let selectedOption = columnSelectionOptions.value.find(option => option.value == columnSelectionCode.value);
  if (selectedOption == null)
    columnSelectionCode.value = "selectedColumn";
});

// Show a checkbox instead of a dropdown for system settings, or when there are fewer than three bins.
let showingCheckbox = computed(() => {
  let bins = props.tag.$session.system.bins.children;
  let bin = props.tag.$parents().find(parent => bins.includes(parent));
  let isSystemSetting = (bin == null);
  let allSystems = map_dropNulls(store.sessions, session => session.system);
  let allBins = allSystems.flatMap(system => system.bins.children);
  return isSystemSetting || allBins.length < 3;
});

let checkboxLabel = computed(() => {
  let system = props.tag.$session.system;
  let bin = props.tag.$parents().find(parent => system.bins.children.includes(parent));
  return (bin == null) ? "Apply to all systems" : "Apply to all bins";
});

let areAllColumnsSelected = computed(() => {
  return columnSelectionCode.value == "allColumns";
});

function setAllColumnsSelected(value) {
  columnSelectionCode.value = (value) ? "allColumns" : "selectedColumn";
}
</script>

<style>
/* .binSelection .p-button {
  padding-left: 12px;
  padding-right: 12px;
}
.binSelection span.p-dropdown-label.p-inputtext {
  padding-right: 0px;
  padding-top: 6px;
  padding-bottom: 5px;
  font-size: small;
} */
</style>
